// src/features/chat/views/ChatInterface.jsx
/*
 * Private chat HIS: one surface inside chat. Messages and Zero-effort
 * artifacts (future-action commitments, approval cards, effect receipts) are
 * peer elements of the flow, interleaved in time order — a display-only merge;
 * artifacts remain a separate control-plane read model and never become chat
 * messages.
 *
 * Legacy compatibility:
 * - Handles legacy message types (search_results/draft_post) no longer emitted by Genkit.
 * - Retained only for backward compatibility with existing data.
 * - Remove after legacy data migration/cleanup is complete.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { auth } from '../../../firebase';
import ChatPostList from '../../../components/ChatPostList';
import DraftItem from '../../../components/DraftItem';
import RecordDraftMessage from '../components/RecordDraftMessage';
import RecordCitationMarkdown from '../components/RecordCitationMarkdown';
import botIcon from '../../../assets/icon.png';
import { SenderMeta, getSenderDisplay } from '../components/SenderMeta';
import layoutStyles from '../styles/ChatLayout.module.css';
import messageStyles from '../styles/Message.module.css';
import hisStyles from '../styles/HisArtifact.module.css';

// Unified chat feature
import {
  useAssistantChat,
  useZeroEffortInbox,
  useArtifactFreshness,
  useHisActions,
  privateTarget,
  MessageList,
  PendingAssistantBubble,
  Composer,
  ApprovalCard,
  ReceiptCard,
  FutureActionCommitmentCard,
  WhisperFold,
  HisDateDivider,
  StarterSuggestions,
  ReplyChips,
  buildHisTimeline,
  insertDateDividers,
  foldSettledRuns,
  latestAssistantAnchorId,
  approvalDisplayState,
  withTerminalOverlay,
  approveAssistantApprovalRequest,
  isPrimitiveRejectionError,
  preflightPrimitiveTap,
  rejectAssistantApprovalRequest,
  selectAssistantInteractionPrimitive,
  undoAssistantEffectReceipt,
} from '..';

// Analytics
import { logCustomEvent, setAnalyticsUserProperties } from '../../../services/analytics/analytics';
import { ANALYTICS_EVENTS } from '../../../services/analytics/events';

const MESSAGES_PER_PAGE = 25;
const SUCCESSFUL_REPLY_GENERATION_STATUSES = new Set([
  'generated',
  'no_primitives',
  'reused',
]);

function replyGenerationNeedsNotice(status) {
  return typeof status === 'string' &&
    status.length > 0 &&
    !SUCCESSFUL_REPLY_GENERATION_STATUSES.has(status);
}

/**
 * Private chat interface component.
 */
const ChatInterface = () => {
  const { t, i18n } = useTranslation();

  // Get locale and timezone for context
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions()?.timeZone;
    } catch {
      return undefined;
    }
  }, []);

  // Create private chat target
  const target = useMemo(() => privateTarget(), []);

  // Use unified chat hook
  const chat = useAssistantChat(target, {
    pageSize: MESSAGES_PER_PAGE,
    locale,
    timezone,
  });
  const [primitiveNotice, setPrimitiveNotice] = useState(null);
  const [composerSeed, setComposerSeed] = useState(null);
  // Fold expansion is membership-based (member item ids, not fold ids), so
  // it survives a run extending in either direction.
  const [expandedFoldItemIds, setExpandedFoldItemIds] = useState(() => new Set());
  const composerSeedIdRef = useRef(0);
  const primitiveSelectionRequestSeqRef = useRef(0);
  const surfaceContextKey = useMemo(() => {
    const latestMessage = [...chat.messages]
      .reverse()
      .find((message) => !message.isOptimistic);
    if (!latestMessage) return 'empty';
    return `${latestMessage.id}:${latestMessage.tsMs || 0}`;
  }, [chat.messages]);
  const zeroEffortInbox = useZeroEffortInbox({
    userId: auth.currentUser?.uid,
    locale,
    timezone,
    surfaceContextKey,
  });

  // Artifacts are independent peer elements: a card stays usable while the
  // assistant is talking (commit-time preconditions make that safe). Only a
  // rate limit gates actions.
  const artifactActionsDisabled = !!chat.rateLimit;
  const actions = useHisActions({
    approvalRequests: zeroEffortInbox.approvalRequests,
    onUndoReceipt: undoAssistantEffectReceipt,
    onApproveRequest: approveAssistantApprovalRequest,
    onRejectRequest: rejectAssistantApprovalRequest,
    disabled: artifactActionsDisabled,
  });

  // The conversational frontier moves on a user send (incl. optimistic).
  // The optimistic → server message-id reconciliation is the same send, so
  // the committed id is aliased back to its optimistic key — the frontier
  // must not move twice for one turn.
  const sendKeyAliasRef = useRef(new Map());
  const lastUserMessageId = useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i -= 1) {
      const message = chat.messages[i];
      if (message.role !== 'user') continue;
      if (message.isOptimistic) {
        if (message.serverMessageId) {
          sendKeyAliasRef.current.set(message.serverMessageId, message.id);
        }
        return message.id;
      }
      return sendKeyAliasRef.current.get(message.id) || message.id;
    }
    return null;
  }, [chat.messages]);

  const freshness = useArtifactFreshness({
    receipts: zeroEffortInbox.effectReceipts,
    approvals: zeroEffortInbox.approvalRequests,
    artifactsLoaded: zeroEffortInbox.artifactsLoaded,
    lastUserMessageId,
  });

  // Settled whispers (and only those) fold; pending cards, fresh artifacts,
  // just-resolved approvals, and failure/conflict traces break runs and never
  // fold. Whisper expansion happens in place and never restructures runs.
  const isFoldable = useCallback(
    (item) => {
      if (item.kind === 'receipt') {
        return !freshness.freshIds.has(item.id);
      }
      if (item.kind === 'approval') {
        // A live terminal overlay means the resolution just happened in front
        // of the user — the whisper stays at the frontier, unfolded.
        if (actions.terminalResultsById[item.artifactId]) return false;
        if (freshness.freshIds.has(item.id)) return false;
        const state = approvalDisplayState(item.approval);
        return state === 'resolved_confirmed' || state === 'resolved_declined';
      }
      return false;
    },
    [freshness.freshIds, actions.terminalResultsById]
  );

  const timeline = useMemo(() => {
    const merged = buildHisTimeline({
      messages: chat.messages,
      commitments: zeroEffortInbox.futureActionCommitments,
      receipts: zeroEffortInbox.effectReceipts,
      approvals: zeroEffortInbox.approvalRequests,
      // History depth is unknown until the first feed snapshot; artifacts
      // must not flash unhidden before the message page lands.
      hasMoreMessages: chat.hasMore || !chat.initialLoaded,
    });
    return foldSettledRuns(insertDateDividers(merged), { isFoldable });
  }, [
    chat.messages,
    chat.hasMore,
    chat.initialLoaded,
    zeroEffortInbox.futureActionCommitments,
    zeroEffortInbox.effectReceipts,
    zeroEffortInbox.approvalRequests,
    isFoldable,
  ]);

  // Replies live on the conversational frontier only.
  const replyAnchorId = useMemo(() => latestAssistantAnchorId(timeline), [timeline]);

  // Handle send with analytics
  const handleSend = useCallback(
    async ({ text, files, primitiveSelectionId }) => {
      try {
        await chat.send({ text, files, primitiveSelectionId });
      } catch (err) {
        console.error('Error sending message:', err);
        throw err;
      }

      // Log analytics
      logCustomEvent(ANALYTICS_EVENTS.SEND_MESSAGE, {
        message_length: text?.trim()?.length || 0,
        image_count: files?.length || 0,
        language: i18n.language,
      });
      setAnalyticsUserProperties({ has_sent_message: 'true' });
    },
    [chat, i18n.language]
  );

  // Returns false only when the tapped primitive itself was rejected as
  // unavailable (locally or by the server) — the signal that the frozen
  // displayed set is stale. Transient callable failures return true so
  // retryable chips stay in place.
  const handlePrimitiveSelect = useCallback(
    async (primitive) => {
      const requestSeq = primitiveSelectionRequestSeqRef.current + 1;
      primitiveSelectionRequestSeqRef.current = requestSeq;
      const preflight = preflightPrimitiveTap(primitive);
      if (!preflight.ok) {
        setPrimitiveNotice('Suggestion no longer available.');
        return false;
      }

      try {
        const selection = await selectAssistantInteractionPrimitive(
          preflight.primitiveId
        );
        if (primitiveSelectionRequestSeqRef.current !== requestSeq) {
          return true;
        }
        setPrimitiveNotice(null);
        composerSeedIdRef.current += 1;
        setComposerSeed({
          id: composerSeedIdRef.current,
          text: preflight.text,
          primitiveSelectionId: selection.selectionId,
        });
        return true;
      } catch (err) {
        if (primitiveSelectionRequestSeqRef.current !== requestSeq) {
          return true;
        }
        console.error('Error selecting primitive:', err);
        if (isPrimitiveRejectionError(err)) {
          setPrimitiveNotice('Suggestion no longer available.');
          return false;
        }
        setPrimitiveNotice('Suggestion could not be selected. Please try again.');
        return true;
      }
    },
    []
  );

  // Recovery seeds (terminal traces, lapsed undo) seed the composer as the
  // user's own editable words — send stays the single commit gesture.
  const handleSeedComposer = useCallback((text) => {
    if (typeof text !== 'string' || !text.trim()) return;
    composerSeedIdRef.current += 1;
    setComposerSeed({
      id: composerSeedIdRef.current,
      text,
      primitiveSelectionId: null,
    });
  }, []);

  const toggleFold = useCallback((fold) => {
    setExpandedFoldItemIds((current) => {
      const memberIds = (fold.items || []).map((member) => member.id);
      const expanded = memberIds.some((id) => current.has(id));
      const next = new Set(current);
      memberIds.forEach((id) => {
        if (expanded) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });
      return next;
    });
  }, []);

  const primitiveRuntimeNotice = useMemo(() => {
    const runtime = chat.assistantRuntime?.interactionPrimitives;
    if (runtime?.selectionResolution?.kind === 'selection_not_used') {
      return 'Suggestion attribution was unavailable, so the message was sent normally.';
    }
    if (replyGenerationNeedsNotice(runtime?.replyGeneration?.status)) {
      return 'Reply suggestions could not be refreshed for this response.';
    }
    return null;
  }, [chat.assistantRuntime]);

  const zeroEffortNotice = zeroEffortInbox.error
    ? 'Assistant suggestions could not load.'
    : zeroEffortInbox.surfaceOpenError
      ? 'Assistant suggestions could not refresh.'
      : null;

  const botName = t('app_name') || 'Assistant';

  const actionNoticeFor = useCallback(
    (keys) => (
      actions.actionNotice && keys.includes(actions.actionNotice.key)
        ? actions.actionNotice.text
        : null
    ),
    [actions.actionNotice]
  );

  // Render a bare artifact (no row wrapper) — also used inside folds.
  const renderArtifactBody = useCallback(
    (item, { showMeta = true } = {}) => {
      if (item.kind === 'receipt') {
        return (
          <ReceiptCard
            key={item.id}
            receipt={item.receipt}
            fresh={freshness.freshIds.has(item.id)}
            expanded={freshness.expandedWhisperIds.has(item.id)}
            born={freshness.bornIds.has(item.id)}
            undoBusy={actions.busyKeys.has(`receipt:${item.artifactId}`)}
            disabled={artifactActionsDisabled}
            notice={actionNoticeFor([`receipt:${item.artifactId}`])}
            showMeta={showMeta}
            onToggle={() => freshness.toggleWhisper(item.id)}
            onUndo={actions.runUndo}
            onSeedComposer={handleSeedComposer}
            botName={botName}
            timeMs={item.sortMs}
          />
        );
      }
      if (item.kind === 'commitment') {
        return (
          <FutureActionCommitmentCard
            key={item.id}
            commitment={item.commitment}
            born={freshness.bornIds.has(item.id)}
            showMeta={showMeta}
            botName={botName}
            timeMs={item.sortMs}
          />
        );
      }
      const approval = withTerminalOverlay(
        item.approval,
        actions.terminalResultsById[item.artifactId]
      );
      return (
        <ApprovalCard
          key={item.id}
          approval={approval}
          born={freshness.bornIds.has(item.id)}
          confirmBusy={actions.busyKeys.has(`approval-approve:${item.artifactId}`)}
          declineBusy={actions.busyKeys.has(`approval-reject:${item.artifactId}`)}
          disabled={artifactActionsDisabled}
          notice={actionNoticeFor([
            `approval-approve:${item.artifactId}`,
            `approval-reject:${item.artifactId}`,
          ])}
          showMeta={showMeta}
          onDecision={actions.runDecision}
          onSeedComposer={handleSeedComposer}
          botName={botName}
          timeMs={item.sortMs}
        />
      );
    },
    [
      freshness,
      actions,
      artifactActionsDisabled,
      actionNoticeFor,
      handleSeedComposer,
      botName,
    ]
  );

  // Replies are the live turn's exits: when the frontier turn carries
  // artifacts, the chips land after the turn's trailing artifacts, not
  // between the prose and the slip it slid across.
  const replyChipsAfterItemId = useMemo(() => {
    if (!replyAnchorId) return null;
    const anchorIndex = timeline.findIndex(
      (item) => item.kind === 'message' && item.message.id === replyAnchorId
    );
    if (anchorIndex === -1) return null;
    let lastId = timeline[anchorIndex].id;
    for (let i = anchorIndex + 1; i < timeline.length; i += 1) {
      const kind = timeline[i].kind;
      if (
        kind === 'commitment' ||
        kind === 'receipt' ||
        kind === 'approval' ||
        kind === 'fold'
      ) {
        lastId = timeline[i].id;
        continue;
      }
      break;
    }
    return lastId;
  }, [timeline, replyAnchorId]);

  // Render individual timeline item (message, artifact, divider, fold)
  const renderTimelineItem = useCallback(
    (item, index) => {
      // Speech identity: artifacts and folds never re-introduce the speaker;
      // only another speaker or a date divider breaks a sender sequence.
      const previousMessage = () => {
        for (let i = index - 1; i >= 0; i -= 1) {
          const earlier = timeline[i];
          if (earlier.kind === 'divider') return null;
          if (earlier.kind === 'message') return earlier.message;
        }
        return null;
      };

      let content = null;

      if (item.kind === 'divider') {
        content = <HisDateDivider dateMs={item.dateMs} />;
      } else if (item.kind === 'fold') {
        content = (
          <div className={hisStyles.row}>
            <div className={hisStyles.slot}>
              <WhisperFold
                fold={item}
                expanded={item.items.some((member) => expandedFoldItemIds.has(member.id))}
                onToggle={() => toggleFold(item)}
                renderItem={renderArtifactBody}
              />
            </div>
          </div>
        );
      } else if (
        item.kind === 'commitment' ||
        item.kind === 'receipt' ||
        item.kind === 'approval'
      ) {
        // Attached artifacts hug the turn (or burst) that produced them and
        // let the adjacent assistant identity speak for them.
        const prev = timeline[index - 1];
        const attached = Boolean(
          prev &&
          (prev.kind === 'commitment' ||
            prev.kind === 'receipt' ||
            prev.kind === 'approval' ||
            prev.kind === 'fold' ||
            (prev.kind === 'message' && prev.message.role === 'assistant'))
        );
        content = (
          <div
            className={hisStyles.row}
            data-his-artifact={item.kind}
            data-attached={attached ? 'true' : 'false'}
          >
            <div className={hisStyles.slot}>
              {renderArtifactBody(item, { showMeta: !attached })}
            </div>
          </div>
        );
      } else {
        const message = item.message;
        const isCurrentUser = message.isOptimistic ||
          message.senderId === auth.currentUser?.uid;
        const isAssistant = message.role === 'assistant';
        const variant = isCurrentUser ? 'sent' : 'received';
        const senderInfo = getSenderDisplay(message, null, t('chat.user'), botName);

        const prevMessage = previousMessage();
        const isFirstInSequence =
          !prevMessage || prevMessage.senderId !== message.senderId;
        const showSender = !isCurrentUser && isAssistant && isFirstInSequence;

        content = (
          <div
            className={messageStyles.row}
            data-variant={variant}
            data-has-avatar={showSender ? 'true' : 'false'}
            data-turn-start={isFirstInSequence ? 'true' : 'false'}
          >
            {/* Avatar and name for assistant messages */}
            {showSender && (
              <SenderMeta
                sender={senderInfo}
                botIcon={botIcon}
                botName={botName}
              />
            )}

            {/* Message content by type */}
            {renderMessageContent(message, { t, isCurrentUser, isAssistant })}
          </div>
        );
      }

      const carriesReplyChips = replyAnchorId && item.id === replyChipsAfterItemId;
      return (
        <React.Fragment key={item.id}>
          {content}
          {carriesReplyChips && (
            <ReplyChips
              anchorId={replyAnchorId}
              primitives={
                zeroEffortInbox.replyPrimitivesByAnchorId?.get(replyAnchorId) || []
              }
              dimmed={chat.sending || Boolean(chat.pendingAssistant)}
              disabled={!!chat.rateLimit}
              onSelect={handlePrimitiveSelect}
            />
          )}
        </React.Fragment>
      );
    },
    [
      timeline,
      expandedFoldItemIds,
      toggleFold,
      renderArtifactBody,
      replyAnchorId,
      replyChipsAfterItemId,
      zeroEffortInbox.replyPrimitivesByAnchorId,
      chat.rateLimit,
      chat.sending,
      chat.pendingAssistant,
      handlePrimitiveSelect,
      t,
      botName,
    ]
  );

  return (
    <div className={layoutStyles.surface}>
      {/* Send-blocking errors keep the loud banner; sub-attention plumbing
          (suggestions, attribution) speaks quietly — the PA never raises
          its voice over an Offer-tier hiccup. */}
      {chat.error && <div className={layoutStyles.notice}>{chat.error}</div>}
      {primitiveNotice && (
        <div className={layoutStyles.quietNotice}>{primitiveNotice}</div>
      )}
      {primitiveRuntimeNotice && (
        <div className={layoutStyles.quietNotice}>{primitiveRuntimeNotice}</div>
      )}
      {zeroEffortNotice && (
        <div className={layoutStyles.quietNotice}>{zeroEffortNotice}</div>
      )}
      {chat.rateLimit && (
        <div className={layoutStyles.notice}>
          {chat.rateLimit.message}
          {chat.rateLimit.resetAt && (
            <span>
              {' '}
              {t('chat.try_again_at', {
                time: chat.rateLimit.resetAt.toLocaleTimeString(),
              })}
            </span>
          )}
        </div>
      )}

      {/* One surface: messages and artifacts interleaved in time order */}
      <MessageList
        messages={timeline}
        hasMore={chat.hasMore}
        loadingMore={chat.loadingMore}
        onLoadMore={chat.loadMore}
        renderMessage={renderTimelineItem}
      >
        {/* Pending assistant bubble */}
        {chat.pendingAssistant && (
          <PendingAssistantBubble
            pending={chat.pendingAssistant}
            botIcon={botIcon}
            botName={botName}
          />
        )}

        {/* Pending drafts */}
        {chat.pendingDrafts?.map((draft) => (
          <div
            key={`pending-draft-${draft.draftId}`}
            className={messageStyles.row}
            data-variant="received"
            data-has-avatar="true"
          >
            <SenderMeta
              sender={{ displayName: botName, isAssistant: true }}
              botIcon={botIcon}
              botName={botName}
            />
            <div className={messageStyles.cardSlot}>
              <RecordDraftMessage draftId={draft.draftId} />
            </div>
          </div>
        ))}
      </MessageList>

      {/* Composer with starters in the composer zone (idle-only) */}
      <Composer
        onSend={handleSend}
        disabled={!!chat.rateLimit}
        sending={chat.sending}
        placeholder={t('chat.type_message')}
        seedMessage={composerSeed}
        topSlot={({ isEmpty }) => (
          <StarterSuggestions
            primitives={zeroEffortInbox.starterPrimitives}
            composerEmpty={isEmpty}
            assistantIdle={!chat.sending && !chat.pendingAssistant}
            disabled={!!chat.rateLimit}
            onSelect={handlePrimitiveSelect}
          />
        )}
      />
    </div>
  );
};

/**
 * Render message content based on type.
 */
function renderMessageContent(message, { t, isCurrentUser, isAssistant }) {
  // Legacy message types (search_results/draft_post) are no longer emitted by Genkit.
  // Retained only for backward compatibility with existing data; remove after legacy data migration/cleanup.
  switch (message.uiType) {
    case 'search_results':
      return (
        <div className={messageStyles.cardSlot}>
          <ChatPostList postIds={message.postIds} />
        </div>
      );

    case 'draft_post':
      return (
        <div className={messageStyles.cardSlot}>
          <DraftItem draftId={message.draftId} />
        </div>
      );

    case 'record_draft':
      return (
        <div className={messageStyles.cardSlot}>
          <RecordDraftMessage draftId={message.draftId} />
        </div>
      );

    case 'chat':
    default: {
      return (
        <div className={messageStyles.bubble}>
          {/* Images */}
          {message.images?.length > 0 &&
            message.images.map((image, idx) => (
              <img
                key={idx}
                src={image.url}
                alt={image.name || t('chat.uploaded_image', { index: idx + 1 })}
                className={messageStyles.image}
                loading="lazy"
              />
            ))}

          {/* Text */}
          <RecordCitationMarkdown
            text={message.text}
            recordCitations={
              !isCurrentUser && isAssistant ?
                message.recordCitations :
                []
            }
          />

        </div>
      );
    }
  }
}

export default ChatInterface;
